import { CONNECTION_STATE_CHANGE, ConnectionState, events } from "@aws-amplify/api-graphql";
import { Hub } from "aws-amplify/utils";
import { createContext, type FC, type ReactNode, useCallback, useRef } from "react";

export type EventHandler = (payload: unknown) => void;

/**
 * subscribe(channel, handler) → returns an unsubscribe function.
 * Guaranteed: for a given channel string, only one AppSync WebSocket subscription
 * is open at a time regardless of how many React components call subscribe.
 */
type Subscribe = (channel: string, handler: EventHandler) => () => void;

export const DocumentEventsContext = createContext<Subscribe | null>(null);

const MAX_RECONNECT_DELAY_MS = 30_000;

type ChannelEntry = {
  handlers: Set<EventHandler>;
  conn: Awaited<ReturnType<typeof events.connect>> | null;
  cancelled: boolean;
  retryCount: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  stopConnectionListener: (() => void) | null;
};

/**
 * Manages a single AppSync Events WebSocket connection per active channel.
 * Mount once near the app root (after Amplify is configured).
 */
export const DocumentEventsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const channelsRef = useRef<Map<string, ChannelEntry>>(new Map());

  const subscribe = useCallback<Subscribe>((channel, handler) => {
    const channels = channelsRef.current;
    let entry = channels.get(channel);

    if (!entry) {
      const newEntry: ChannelEntry = {
        handlers: new Set(),
        conn: null,
        cancelled: false,
        retryCount: 0,
        retryTimer: null,
        stopConnectionListener: null,
      };

      channels.set(channel, newEntry);
      entry = newEntry;

      const connectionStates = new Map<unknown, string>();
      newEntry.stopConnectionListener = Hub.listen("api", ({ payload }) => {
        if (payload.event !== CONNECTION_STATE_CHANGE) return;
        const data = payload.data;
        if (
          !data ||
          typeof data !== "object" ||
          !("connectionState" in data) ||
          typeof data.connectionState !== "string"
        )
          return;
        const state = data.connectionState;
        const provider = "provider" in data ? data.provider : undefined;
        const previous = connectionStates.get(provider);
        connectionStates.set(provider, state);
        if (state === ConnectionState.Connected && previous && previous !== state)
          newEntry.handlers.forEach((handler) => handler({ kind: "reconnected" }));
      });

      const scheduleReconnect = (err: unknown) => {
        if (newEntry.cancelled) return;
        console.error(`[DocumentEvents] channel error on ${channel}`, err);
        newEntry.conn = null;
        const delay = Math.min(1_000 * 2 ** newEntry.retryCount, MAX_RECONNECT_DELAY_MS);
        newEntry.retryCount++;
        newEntry.retryTimer = setTimeout(() => openChannel(), delay);
      };

      const openChannel = () => {
        void events
          .connect(channel, { authMode: "userPool" })
          .then((conn) => {
            if (newEntry.cancelled) {
              conn.close();

              return;
            }
            newEntry.conn = conn;
            const reconnected = newEntry.retryCount > 0;
            newEntry.retryCount = 0;
            conn.subscribe({
              next: (rawData: unknown) => {
                // AppSync Events wraps payloads: { event: <actualPayload> }
                const payload =
                  typeof rawData === "object" && rawData !== null && "event" in rawData
                    ? rawData.event
                    : rawData;
                newEntry.handlers.forEach((h) => h(payload));
              },
              error: scheduleReconnect,
            });
            if (reconnected)
              newEntry.handlers.forEach((handler) => handler({ kind: "reconnected" }));
          })
          .catch(scheduleReconnect);
      };

      openChannel();
    }

    entry.handlers.add(handler);

    return () => {
      const e = channels.get(channel);
      if (!e) return;

      e.handlers.delete(handler);
      if (e.handlers.size === 0) {
        e.cancelled = true;
        e.stopConnectionListener?.();
        if (e.retryTimer !== null) clearTimeout(e.retryTimer);
        e.conn?.close();
        channels.delete(channel);
      }
    };
  }, []);

  return (
    <DocumentEventsContext.Provider value={subscribe}>{children}</DocumentEventsContext.Provider>
  );
};
